/**
 * Theme Editor Component
 * 
 * Form for creating and editing color themes.
 * Supports both categorical and range-based mappings with color picker.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Select,
  Checkbox,
  Button,
  Alert,
  Container,
  Grid,
} from '@cloudscape-design/components';
import {
  createAdminTheme,
  updateAdminTheme,
  createUserTheme,
  updateUserTheme,
} from '../../services/themeApi';
import {
  ColorTheme,
  ColorMapping,
  MappingType,
  DataProduct,
  CreateThemeRequest,
  UpdateThemeRequest,
} from '../../types/themes';

interface ThemeEditorProps {
  theme?: ColorTheme | null;
  productId?: string;
  attributeId?: string;
  products: DataProduct[];
  isSystemTheme: boolean;
  isDuplicating?: boolean;  // When true, treat theme as template for new theme
  onClose: (saved: boolean) => void;
}

const ThemeEditor: React.FC<ThemeEditorProps> = ({
  theme,
  productId: initialProductId,
  attributeId: initialAttributeId,
  products,
  isSystemTheme,
  isDuplicating = false,
  onClose,
}) => {
  const [themeName, setThemeName] = useState(
    isDuplicating && theme ? `${theme.theme_name} (Copy)` : (theme?.theme_name || '')
  );
  const [productId, setProductId] = useState(initialProductId || theme?.data_product || '');
  const [attributeId, setAttributeId] = useState(initialAttributeId || theme?.attribute || '');
  const [mappingType, setMappingType] = useState<MappingType>(theme?.mapping_type || 'categorical');
  const [mappings, setMappings] = useState<ColorMapping[]>(theme?.mappings || []);
  const [isDefault, setIsDefault] = useState(theme?.is_default || false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Get selected product and attribute
  const selectedProduct = products.find(p => p.product_id === productId);
  const selectedAttribute = selectedProduct?.attributes.find(a => a.attribute_id === attributeId);

  // Initialize with empty mapping if none exist
  useEffect(() => {
    if (mappings.length === 0) {
      addMapping();
    }
  }, []);

  const addMapping = () => {
    const newMapping: ColorMapping = mappingType === 'categorical'
      ? { category: '', color: '#22c55e', label: '' }
      : { min: 0, max: 10, color: '#22c55e', label: '' };
    setMappings([...mappings, newMapping]);
  };

  const removeMapping = (index: number) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const updateMapping = (index: number, field: keyof ColorMapping, value: any) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
  };

  const validateForm = (): string | null => {
    if (!themeName.trim()) {
      return 'Theme name is required';
    }
    if (!productId) {
      return 'Product is required';
    }
    if (!attributeId) {
      return 'Attribute is required';
    }
    if (mappings.length === 0) {
      return 'At least one mapping is required';
    }

    for (let i = 0; i < mappings.length; i++) {
      const mapping = mappings[i];
      
      if (!mapping.color || !/^#[0-9A-Fa-f]{6}$/.test(mapping.color)) {
        return `Mapping ${i + 1}: Invalid color format (must be #RRGGBB)`;
      }
      
      if (!mapping.label.trim()) {
        return `Mapping ${i + 1}: Label is required`;
      }

      if (mappingType === 'categorical') {
        if (!mapping.category?.trim()) {
          return `Mapping ${i + 1}: Category is required`;
        }
      } else {
        if (mapping.min === undefined || mapping.max === undefined) {
          return `Mapping ${i + 1}: Min and max values are required`;
        }
        if (mapping.min >= mapping.max) {
          return `Mapping ${i + 1}: Min must be less than max`;
        }
      }
    }

    // Check for overlapping ranges
    if (mappingType === 'range') {
      for (let i = 0; i < mappings.length; i++) {
        for (let j = i + 1; j < mappings.length; j++) {
          const m1 = mappings[i];
          const m2 = mappings[j];
          if (m1.min! < m2.max! && m2.min! < m1.max!) {
            return `Mappings ${i + 1} and ${j + 1} have overlapping ranges`;
          }
        }
      }
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (theme && !isDuplicating) {
        // Update existing theme
        const request: UpdateThemeRequest = {
          theme_name: themeName,
          mappings,
          is_default: isDefault,
        };
        
        if (isSystemTheme) {
          await updateAdminTheme(theme.theme_id, request);
        } else {
          await updateUserTheme(theme.theme_id, request);
        }
      } else {
        // Create new theme
        const request: CreateThemeRequest = {
          theme_name: themeName,
          data_product: productId,
          attribute: attributeId,
          mapping_type: mappingType,
          mappings,
          is_default: isDefault,
        };
        
        if (isSystemTheme) {
          await createAdminTheme(request);
        } else {
          await createUserTheme(request);
        }
      }

      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={true}
      onDismiss={() => onClose(false)}
      header={isDuplicating ? 'Duplicate Theme' : (theme ? 'Edit Theme' : 'Create Theme')}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() => onClose(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
            >
              {theme && !isDuplicating ? 'Update' : 'Create'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="l">
          {error && (
            <Alert type="error" dismissible onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}
          <FormField
            label="Theme Name"
            description="A descriptive name for this color theme"
          >
            <Input
              value={themeName}
              onChange={(e) => setThemeName(e.detail.value)}
              placeholder="e.g., METAR Flight Category - Default"
            />
          </FormField>

          <FormField
            label="Data Product"
            description={theme ? "Cannot be changed after creation" : "Select the data product this theme applies to"}
          >
            <Select
              selectedOption={
                productId
                  ? { label: products.find(p => p.product_id === productId)?.display_name || '', value: productId }
                  : null
              }
              onChange={(e) => {
                setProductId(e.detail.selectedOption.value || '');
                setAttributeId('');
              }}
              options={products.filter(p => p.is_active).map(p => ({
                label: p.display_name,
                value: p.product_id
              }))}
              placeholder="Select a product"
              disabled={!!theme && !isDuplicating}
            />
          </FormField>

          <FormField
            label="Attribute"
            description={theme ? "Cannot be changed after creation" : "Select the attribute this theme applies to"}
          >
            <Select
              selectedOption={
                attributeId && selectedProduct
                  ? {
                      label: `${selectedProduct.attributes.find(a => a.attribute_id === attributeId)?.display_name || ''} (${selectedAttribute?.data_type || ''})`,
                      value: attributeId
                    }
                  : null
              }
              onChange={(e) => setAttributeId(e.detail.selectedOption.value || '')}
              options={selectedProduct?.attributes.filter(a => a.is_active).map(a => ({
                label: `${a.display_name} (${a.data_type})`,
                value: a.attribute_id
              })) || []}
              placeholder="Select an attribute"
              disabled={(!!theme && !isDuplicating) || !productId}
            />
          </FormField>

          <FormField
            label="Mapping Type"
            description={
              mappingType === 'categorical'
                ? 'For text-based categories like VFR, MVFR, IFR'
                : 'For numeric ranges like 0-10 kt, 10-20 kt'
            }
          >
            <Select
              selectedOption={{
                label: mappingType === 'categorical' ? 'Categorical (discrete values)' : 'Range-based (numeric ranges)',
                value: mappingType
              }}
              onChange={(e) => {
                setMappingType(e.detail.selectedOption.value as MappingType);
                setMappings([]);
              }}
              options={[
                { label: 'Categorical (discrete values)', value: 'categorical' },
                { label: 'Range-based (numeric ranges)', value: 'range' }
              ]}
              disabled={!!theme && !isDuplicating}
            />
          </FormField>

          {isSystemTheme && (
            <FormField>
              <Checkbox
                checked={isDefault}
                onChange={(e) => setIsDefault(e.detail.checked)}
              >
                Set as default theme for this product/attribute
              </Checkbox>
            </FormField>
          )}

          <Container
            header={
              <Box float="right">
                <Button onClick={addMapping} iconName="add-plus">
                  Add Mapping
                </Button>
              </Box>
            }
          >
            <SpaceBetween size="m">
              {mappings.map((mapping, index) => (
                <Container key={index}>
                  <Grid gridDefinition={[
                    { colspan: mappingType === 'categorical' ? 3 : 2 },
                    { colspan: mappingType === 'categorical' ? 3 : 2 },
                    { colspan: mappingType === 'categorical' ? 4 : 4 },
                    { colspan: 2 }
                  ]}>
                    {mappingType === 'categorical' ? (
                      <FormField label="Category">
                        <Input
                          value={mapping.category || ''}
                          onChange={(e) => updateMapping(index, 'category', e.detail.value)}
                          placeholder="e.g., VFR"
                        />
                      </FormField>
                    ) : (
                      <>
                        <FormField label="Min">
                          <Input
                            type="number"
                            value={mapping.min?.toString() || ''}
                            onChange={(e) => updateMapping(index, 'min', parseFloat(e.detail.value))}
                            step={0.1}
                          />
                        </FormField>
                        <FormField label="Max">
                          <Input
                            type="number"
                            value={mapping.max?.toString() || ''}
                            onChange={(e) => updateMapping(index, 'max', parseFloat(e.detail.value))}
                            step={0.1}
                          />
                        </FormField>
                      </>
                    )}

                    <FormField label="Color">
                      <SpaceBetween direction="horizontal" size="xs">
                        <input
                          type="color"
                          value={mapping.color}
                          onChange={(e) => updateMapping(index, 'color', e.target.value)}
                          style={{ width: '40px', height: '32px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                        />
                        <Input
                          value={mapping.color}
                          onChange={(e) => updateMapping(index, 'color', e.detail.value)}
                          placeholder="#22c55e"
                        />
                      </SpaceBetween>
                    </FormField>

                    <FormField label="Label">
                      <Input
                        value={mapping.label}
                        onChange={(e) => updateMapping(index, 'label', e.detail.value)}
                        placeholder="e.g., VFR (Visual Flight Rules)"
                      />
                    </FormField>

                    <Box margin={{ top: 'l' }}>
                      <Button
                        onClick={() => removeMapping(index)}
                        disabled={mappings.length === 1}
                        iconName="remove"
                      >
                        Remove
                      </Button>
                    </Box>
                  </Grid>
                </Container>
              ))}
            </SpaceBetween>
          </Container>

          <Container header="Preview">
            <SpaceBetween direction="horizontal" size="m">
              {mappings.map((mapping, index) => (
                <Box key={index}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        backgroundColor: mapping.color
                      }}
                    />
                    <Box fontSize="body-s">
                      {mapping.label || (mappingType === 'categorical' ? mapping.category : `${mapping.min}-${mapping.max}`)}
                    </Box>
                  </SpaceBetween>
                </Box>
              ))}
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default ThemeEditor;
